import React, { useState, useEffect } from "react";
import States from "../../constant/states";
import constant from '../../constant';

const HospitalForm = ({
  initialValues,
  onSubmit,
  onCancel,
  isEditMode = false,
}) => {
  const [hospitalId, setHospitalId] = useState(initialValues?.hospital_id || "");
  const [name, setName] = useState(initialValues?.name || "");
  const [state, setState] = useState(initialValues?.state || "");
  const [pincode, setPincode] = useState(initialValues?.pincode || "");
  const [city, setCity] = useState(initialValues?.city || "");
  const [street, setStreet] = useState(initialValues?.street || "");
  const [logo, setLogo] = useState(null);
  const [logoUrl, setLogoUrl] = useState(initialValues?.logo || "");
  const [createdOn, setCreatedOn] = useState(initialValues?.created_on || "");
  const [createdBy, setCreatedBy] = useState(initialValues?.created_by || "");
  const [isActive, setIsActive] = useState(
    initialValues?.is_active === undefined ? true : initialValues.is_active
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialValues) {
      setHospitalId(initialValues.hospital_id || "");
      setName(initialValues.name || "");
      setState(initialValues.state || "");
      setPincode(initialValues.pincode || "");
      setCity(initialValues.city || "");
      setStreet(initialValues.street || "");
      setLogoUrl(initialValues.logo || "");
      setCreatedOn(initialValues.created_on || "");
      setCreatedBy(initialValues.created_by || "");
      setIsActive(
        initialValues.is_active === undefined ? true : initialValues.is_active
      );
    } else {
      setHospitalId("");
      setName("");
      setState("");
      setPincode("");
      setCity("");
      setStreet("");
      setLogo(null);
      setLogoUrl("");
      setCreatedOn("");
      setCreatedBy("");
      setIsActive(true);
      setErrors({});
    }
  }, [initialValues]);

  const isValidPincode = (pincode) => /^\d{6}$/.test(pincode);

  const validateForm = () => {
    let tempErrors = {};

    if (!name) tempErrors.name = "Name is required";
    if (!state) tempErrors.state = "State is required";
    if (!pincode) tempErrors.pincode = "Pincode is required";
    if (pincode && !isValidPincode(pincode))
      tempErrors.pincode = "Please enter a valid 6-digit pincode";
    if (!city) tempErrors.city = "City is required";
    if (!street) tempErrors.street = "Street is required";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleLogoUpload = async (file) => {
    if (!file) {
      setErrors({ ...errors, logo: "Please select a file to upload" });
      return null;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/upload-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload logo');
      }

      const { url } = await response.json();
      return url;
    } catch (error) {
      setErrors({ ...errors, logo: 'Failed to upload logo' });
      return null;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let finalLogoUrl = logoUrl;
      if (logo) {
        finalLogoUrl = await handleLogoUpload(logo);
        if (!finalLogoUrl) {
          setLoading(false);
          return;
        }
      }

      const hospitalData = {
        name,
        state,
        pincode,
        city,
        street,
        logo: finalLogoUrl,
        is_active: isActive,
      };

      await onSubmit(hospitalData);
    } catch (err) {
      console.error("Form submission error:", err);
      setErrors({
        form: err.message || "Failed to submit the form. Please check console for details.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {errors.form && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 mx-6">
          {errors.form}
        </div>
      )}

      <div className="p-6 space-y-6">
        <div className="grid bg-gray-50 p-6 rounded-lg grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={`w-full px-4 py-2 border ${
                errors.name ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Street */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Street <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              required
              className={`w-full px-4 py-2 border ${
                errors.street ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {errors.street && (
              <p className="mt-1 text-sm text-red-500">{errors.street}</p>
            )}
          </div>

          {/* City */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
              className={`w-full px-4 py-2 border ${
                errors.city ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {errors.city && (
              <p className="mt-1 text-sm text-red-500">{errors.city}</p>
            )}
          </div>

          {/* State */}
          <div>
            <States
              label="State"
              required
              value={state}
              onChange={(e) => setState(e.target.value)}
              error={errors.state}
            />
            {errors.state && (
              <p className="mt-1 text-sm text-red-500">{errors.state}</p>
            )}
          </div>

          {/* Pincode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pincode RPM <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={pincode}
              onChange={(e) => setPincode(e.target.value)}
              required
              className={`w-full px-4 py-2 border ${
                errors.pincode ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {errors.pincode && (
              <p className="mt-1 text-sm text-red-500">{errors.pincode}</p>
            )}
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogo(e.target.files[0])}
              className={`w-full px-4 py-2 border ${
                errors.logo ? "border-red-500" : "border-gray-300"
              } rounded-lg`}
            />
            {logoUrl && (
              <div className="mt-2">
                <img src={logoUrl} alt="Hospital Logo" className="h-20 w-20 object-contain" />
              </div>
            )}
            {errors.logo && (
              <p className="mt-1 text-sm text-red-500">{errors.logo}</p>
            )}
          </div>

          {/* Created On (Read-only) */}
          {isEditMode && createdOn && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created On
              </label>
              <input
                type="text"
                value={new Date(createdOn).toLocaleString()}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>
          )}

          {/* Created By (Read-only) */}
          {isEditMode && createdBy && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created By
              </label>
              <input
                type="text"
                value={createdBy}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>
          )}

          {/* Status */}
          {isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={isActive ? "Active" : "Inactive"}
                onChange={(e) => setIsActive(e.target.value === "Active")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-4 rounded-b-lg sticky bottom-0">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? isEditMode
              ? "Saving..."
              : "Adding..."
            : isEditMode
              ? "Save Changes"
              : "Add Hospital"}
        </button>
      </div>
    </div>
  );
};

export default HospitalForm;